// The Prices page's three tabs in the phone's top bar, where the brand stands on every other page.

import { useLanguage } from "../../i18n/language-provider";
import { routePath, type AppRoute } from "../../navigation/routes";
import { AppLink } from "../navigation/AppLink";
import { cn } from "../ui/utils";
import { currentPricesTab, openPricesTab, pricesPlaceLabels, pricesTabRoute, PRICES_PLACE_TABS } from "./prices-places";

interface PricesTopBarSwitcherProps {
    /** The route on screen; the caller draws the switcher on the Prices page only, and never for the front desk. */
    route: AppRoute;
    /** The shell's move, which checks the page is open to the reader. */
    onNavigate: (route: AppRoute) => void;
}

// TabsTrigger's own colour tokens, so the bar reads as the page's tabs moved up. A label wraps, never truncates.
const SEGMENT =
    "flex min-h-10 w-full items-center justify-center rounded-md border border-transparent px-1.5 py-1 text-center text-xs font-medium leading-tight text-foreground dark:text-muted-foreground transition-colors duration-150 hover:text-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50";
const CURRENT = "bg-card text-foreground border-border dark:bg-input/30 dark:border-input dark:text-foreground";

export function PricesTopBarSwitcher({ route, onNavigate }: PricesTopBarSwitcherProps) {
    const { t } = useLanguage();
    const labels = pricesPlaceLabels(t);
    const current = currentPricesTab(route);

    return (
        <nav aria-label={labels.prices} className="min-w-0 flex-1">
            <ul className="grid grid-cols-3 gap-0.5 rounded-lg bg-muted p-0.5">
                {PRICES_PLACE_TABS.map((tab) => (
                    <li key={tab} className="min-w-0">
                        <AppLink
                            to={routePath(pricesTabRoute(tab))}
                            onNavigate={() => openPricesTab(tab, { route, navigateTo: onNavigate })}
                            active={tab === current}
                            className={cn(SEGMENT, tab === current && CURRENT)}
                        >
                            {labels[tab]}
                        </AppLink>
                    </li>
                ))}
            </ul>
        </nav>
    );
}
